import { Icon } from "@constellation-academy/academies-lib-webapp";
import { IconMap } from "@constellation-academy/epic-ui";
import { isEqual } from "lodash";
import { Fragment, useState } from "react";
import { useLazyLoadQuery } from "react-relay";
import { match } from "ts-pattern";
import { ReactComponent as CorrectAnswerIcon } from "@assets/icons/correct-answer-flag.svg";
import { ReactComponent as KnowledgeBulbSvg } from "@assets/knowledge-bulb.svg";
import { ClozeTextLearnElement } from "@components/cloze-text-learn-element";
import { MarkMistakesLearnElement } from "@components/mark-mistakes-learn-element";
import { MatrixLearnElementComponent } from "@components/matrix-learn-element/matrix-learn-element.component";
import { MultipleChoiceLearnElement } from "@components/multiple-choice-learn-element";
import { OrderLearnElement } from "@components/order-learn-element";
import {
	lessonInsights_Query,
	lessonInsights_Query$data,
} from "@relay/lessonInsights_Query.graphql";
import { ContentFinishedExpandableItemComponent } from "@screens/elearning-content-submission/parts/content-finished-expendable/content-finished-expandable-item.component";
import { error100 } from "@themes/colors";
import { P2Span } from "@themes/font-tags";
import { LessonInsightsProps } from "./lession-insights.types";
import { QUERY } from "./lesson-insights.graphql";
import {
	buttonWrapperClass,
	elementTitleWrapper,
	insightsIconClass,
	itemIconWrapperClass,
	itemSheetWrapperClass,
	itemWrapperClass,
	markMistakesWrapperClass,
	multipleChoiceWrapperClass,
} from "./lesson-insights.styles";

import {
	createCalculateClozeTextStatus,
	createCalculateMarkMistakesStatus,
	createCalculateMultipleChoiceStatus,
	createCalculateOrderStatus,
	getClozeTextUntilFirstClozePart,
} from "./lesson-insights.utils";

type Answer = lessonInsights_Query$data["Viewer"]["Learn"]["getAnswers"][number];

const areEqualUnordered = (a: number[] = [], b: number[] = []) =>
	isEqual([...a].sort(), [...b].sort());

export function isWrongAnswer(answer: Answer): boolean {
	return match(answer)
		.with(
			{ __typename: "MultipleChoiceShowAnswerElementState" },
			(a) =>
				!areEqualUnordered(
					a.correctAnswerIndices as number[],
					a.lastCheckedAnswerIndices as number[],
				),
		)
		.with(
			{ __typename: "ClozeTextShowAnswerElementState" },
			(a) =>
				JSON.stringify(a.correctAnswers ?? []) !==
				JSON.stringify(a.lastCheckedAnswers ?? []),
		)
		.with(
			{ __typename: "OrderShowAnswerElementState" },
			(a) =>
				JSON.stringify(a.correctOrder ?? []) !== JSON.stringify(a.lastSelectedOrder ?? []),
		)
		.with(
			{ __typename: "MatrixShowAnswerElementState" },
			(a) =>
				JSON.stringify(a.correctIndices ?? []) !==
				JSON.stringify(a.lastCheckedIndices ?? []),
		)
		.otherwise(() => true); // Unhandled types considered wrong
}

export const LessonInsights = ({ submissionId }: LessonInsightsProps) => {
	const [openInsightId, setOpenInsightId] = useState<string | null>(null);

	const data = useLazyLoadQuery<lessonInsights_Query>(
		QUERY,
		{
			submissionId,
		},
		{
			fetchPolicy: "network-only",
		},
	);

	const allAnswers = data?.Viewer?.Learn?.getAnswers ?? [];
	const wrongAnswerResults = allAnswers.filter(isWrongAnswer);

	const createOpenInsightOnClickHandler = (elementId: string, isVisible: boolean) => () => {
		setOpenInsightId(isVisible ? null : elementId);
	};

	const renderResultItem = (result: typeof wrongAnswerResults[number], index: number) => {
		const isResultWrong = isWrongAnswer(result);
		const element = match(result)
			.with({ __typename: "ClozeTextShowAnswerElementState" }, (response) => {
				return (
					<ClozeTextLearnElement
						isResultWrong={isResultWrong}
						disabled
						checkedIndices={response.lastCheckedAnswers ?? []}
						answerIds={response.correctAnswers}
						getStatusForIndex={createCalculateClozeTextStatus(
							response.element.parts,
							response.correctAnswers,
							response.lastCheckedAnswers,
						)}
						learnElementFragmentRef={response.element}
					/>
				);
			})
			.with({ __typename: "MarkMistakesShowAnswerElementState" }, (response) => (
				<div className={markMistakesWrapperClass}>
					<MarkMistakesLearnElement
						learnElementFragmentRef={response.element}
						checkedIndices={(response.lastCheckedMistakeIndices as number[]) ?? []}
						getStatusForSnippetByIndex={createCalculateMarkMistakesStatus(
							response.correctMistakeIndices,
							response.lastCheckedMistakeIndices,
						)}
					/>
				</div>
			))
			.with({ __typename: "MultipleChoiceShowAnswerElementState" }, (response) => (
				<div className={multipleChoiceWrapperClass}>
					<MultipleChoiceLearnElement
						isResultWrong={isResultWrong}
						learnElementFragmentRef={response.element}
						checkedIndices={(response.lastCheckedAnswerIndices as number[]) ?? []}
						getStatusForIndex={createCalculateMultipleChoiceStatus(
							response.correctAnswerIndices,
							response.lastCheckedAnswerIndices,
						)}
					/>
				</div>
			))
			.with({ __typename: "OrderShowAnswerElementState" }, (response) => {
				return (
					<OrderLearnElement
						isResultWrong={isResultWrong}
						lastSelectedOrder={response?.lastSelectedOrder}
						learnElementFragmentRef={response.element}
						elementIds={response.correctOrder as string[]}
						getStatusForIndexAndElementId={createCalculateOrderStatus(
							response.correctOrder,
							response.lastSelectedOrder,
						)}
					/>
				);
			})
			.with({ __typename: "MatrixShowAnswerElementState" }, () => {
				return <MatrixLearnElementComponent isResultWrong={isResultWrong} />;
			})
			.otherwise(() => null);

		if (element === null) {
			return null;
		}

		const clozeTextTitle = getClozeTextUntilFirstClozePart(result.element.parts);
		const elementTitle =
			result.element.title ||
			result.element.question ||
			(clozeTextTitle.length > 0 ? clozeTextTitle : `Frage ${index + 1}`);
		const isVisible = openInsightId === result.element.id;

		return (
			<Fragment key={result.element.id}>
				<div className={itemWrapperClass}>
					<button
						className={buttonWrapperClass}
						type="button"
						onClick={createOpenInsightOnClickHandler(result.element.id, isVisible)}
					>
						<div className={itemIconWrapperClass}>
							{isResultWrong ? (
								<Icon
									icon={"close" as keyof IconMap}
									color={error100}
									sizeRem={1}
								/>
							) : (
								<CorrectAnswerIcon />
							)}
						</div>
						<div className={elementTitleWrapper}>
							<P2Span className={"flex text-left"}>{elementTitle}</P2Span>
						</div>
					</button>
					{openInsightId === result.element.id && (
						<div className={itemSheetWrapperClass}>
							<div>{element}</div>
						</div>
					)}
				</div>
			</Fragment>
		);
	};

	if (!allAnswers?.length) {
		return null;
	}

	return (
		<ContentFinishedExpandableItemComponent
			title="Deine Lektions-Insights"
			iconRenderer={<KnowledgeBulbSvg className={insightsIconClass} />}
			iconVariant="blue"
			value={`${allAnswers?.length - wrongAnswerResults?.length} von ${
				allAnswers?.length
			} richtig`}
		>
			{allAnswers?.map(renderResultItem)}
		</ContentFinishedExpandableItemComponent>
	);
};
